import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, Lock, User, Loader2, Eye, EyeOff, MailCheck } from "lucide-react";
import TrueFlowLogo from "../components/TrueFlowLogo";
import { useAuth } from "../context/AuthContext";
import { refActual } from "../lib/referral";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const currencies = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "MXN", name: "Mexican Peso" },
  { code: "CLP", name: "Chilean Peso" },
  { code: "BRL", name: "Brazilian Real" },
  { code: "ARS", name: "Argentine Peso" },
  { code: "COP", name: "Colombian Peso" },
  { code: "PEN", name: "Peruvian Sol" },
  { code: "GBP", name: "British Pound" },
];

// ISO 3166-1 alpha-2 country code → currency code (only currencies we support)
const COUNTRY_CURRENCY = {
  BR: "BRL",
  MX: "MXN",
  CL: "CLP",
  AR: "ARS",
  CO: "COP",
  PE: "PEN",
  GB: "GBP",
  // Euro-zone countries
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", PT: "EUR",
  NL: "EUR", BE: "EUR", AT: "EUR", FI: "EUR", IE: "EUR",
  LU: "EUR", GR: "EUR", SK: "EUR", SI: "EUR", EE: "EUR",
  LV: "EUR", LT: "EUR", MT: "EUR", CY: "EUR",
};

function getDefaultCurrency() {
  try {
    const locale = navigator.language || (navigator.languages && navigator.languages[0]) || "";
    // "pt-BR" → ["pt", "BR"] → region = "BR"
    const parts = locale.split("-");
    if (parts.length > 1) {
      const region = parts[parts.length - 1].toUpperCase();
      if (COUNTRY_CURRENCY[region]) return COUNTRY_CURRENCY[region];
    }
  } catch (_) {}
  return "USD";
}

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consolidationCurrency, setConsolidationCurrency] = useState(getDefaultCurrency);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // El correo al que se mandó el enlace, cuando ya se mandó. El alta ya no
  // termina acá: sigue en /verify-email, con el enlace que llega al correo.
  const [verificationSent, setVerificationSent] = useState(null);
  const { register } = useAuth();
  const { t, i18n } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error(t("common.required"));
      return;
    }

    if (password.length < 8) {
      toast.error(t("auth.passwordLength"));
      return;
    }

    setLoading(true);
    try {
      const result = await register(
        { email, password, name },
        {
          name,
          consolidation_currency: consolidationCurrency,
          default_language: i18n.language,
          // Atribución de partner (?ref= capturado al llegar). Invisible a
          // propósito: no hay campo en el formulario ni verificación pública
          // del código — un endpoint que dijera "código válido/inválido"
          // permitiría enumerar los códigos de los partners.
          partner_code: refActual(),
        }
      );
      // La respuesta es la misma exista el correo o no, así que esta pantalla
      // tampoco puede decir nada distinto según el caso: si dijera "ya tienes
      // cuenta", el backend habría cerrado el oráculo y la UI lo reabriría.
      // Cuando el correo ya está registrado, el aviso va por correo — al dueño
      // de la casilla, que es el único que debería enterarse.
      setVerificationSent(result?.email || email);
    } catch (error) {
      // El 429 se distingue a mano porque slowapi responde `{"error": ...}` y no
      // `{"detail": ...}`, así que caía en el mensaje genérico "Error del
      // servidor" — que además de poco útil es falso: el servidor está bien.
      //
      // Ahora importa más que antes. El alta pasó a tener dos pasos y esta
      // pantalla invita a reintentar ("Usar otro correo"), así que alguien que
      // se equivoca al tipear su correo un par de veces llega al tope de
      // 5/hora sin hacer nada raro. Mismo criterio que ForgotPassword.jsx.
      if (error.response?.status === 429) {
        toast.error(t("auth.rateLimitError"));
      } else {
        toast.error(error.response?.data?.detail || t("errors.serverError"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Link to="/">
              <TrueFlowLogo variant="light" width={440} height={120} />
            </Link>
          </div>
          <Card className="shadow-sm" data-testid="verification-sent-card">
            <CardHeader className="text-center">
              <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <MailCheck className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="font-heading text-2xl">
                {t("auth.verifySentTitle")}
              </CardTitle>
              <CardDescription className="mt-2">
                {t("auth.verifySentBody", { email: verificationSent })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground text-center mb-6">
                {t("auth.verifySentHint")}
              </p>
              <Button
                className="w-full mb-3"
                variant="outline"
                onClick={() => setVerificationSent(null)}
              >
                {t("auth.verifySentRetry")}
              </Button>
              <Button asChild className="w-full" variant="ghost">
                <Link to="/login">{t("auth.backToLogin")}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/">
            <TrueFlowLogo variant="light" width={440} height={120} />
          </Link>
        </div>

        <Card className="shadow-sm" data-testid="register-card">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl">{t("auth.registerTitle")}</CardTitle>
            <CardDescription>{t("auth.registerSubtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("auth.name")}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Tu nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("auth.consolidationCurrency")}</Label>
                <Select value={consolidationCurrency} onValueChange={setConsolidationCurrency}>
                  <SelectTrigger data-testid="register-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} - {t(`auth.currencyNames.${c.code}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("common.loading")}
                  </>
                ) : (
                  t("auth.register")
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              {t("auth.hasAccount")}{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                {t("auth.login")}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
